// src/components/YourReward.js
import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Box,
  Button,
  Link,
  IconButton,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useWallet } from '@solana/wallet-adapter-react';
import { useBalance } from '../context/BalanceContext';
// Firebase Authentication の v9 モジュール方式でインポート
import { getAuth } from 'firebase/auth';

const YourReward = () => {
  const { publicKey } = useWallet();
  const { reward, fetchReward,rewardLoading } = useBalance();

  // ローカルの報酬ステート（Context の reward と同期）
  const [localReward, setLocalReward] = useState(reward);
  useEffect(() => {
    setLocalReward(reward);
  }, [reward]);

  const [withdrawing, setWithdrawing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Withdrawal 処理
  const handleWithdraw = async () => {
    if (!localReward || localReward === 0) return;

    setWithdrawing(true);
    try {
      // Firebase Auth の現在のユーザー情報を取得
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Fail to fetch firebase info');
      }
      // ID トークンの取得
      const idToken = await currentUser.getIdToken();
      const uid = currentUser.uid;

      // ここでは localReward 全額を引き出す想定
      const withdrawalAmount = localReward;

      // バックエンドの /withdraw エンドポイントにリクエスト送信
      const response = await fetch('/withdraw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          uid: uid,
          amount: withdrawalAmount,
        }),
      });
      const result = await response.json();

      if (!response.ok) {
        // サーバーからの error と detail を組み合わせたエラーメッセージを作成
        const errorMsg = result.error
          ? `${result.error}${result.detail ? ' - ' + result.detail : ''}`
          : 'Withdrawal failed';
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      // 成功時は Snackbar で成功メッセージを表示（トランザクションIDが長くても折り返すようにする）
      setSuccessMessage(`Withdrawal successful. Transaction Signature: ${result.transactionSignature} \n It may take several tens of seconds to a few minutes for your updated balance to be reflected.`);

      // ローカルの報酬を更新（全額引き出した場合は 0 に）
      setLocalReward(prev => prev - withdrawalAmount);

      
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setWithdrawing(false);
    }
  };

  // 残高再取得用ハンドラー（リロード）
  const handleReload = async () => {
    try {
      await fetchReward();
      setErrorMessage('');
    } catch (err) {
      setErrorMessage(err.message || 'error is occurred');
    }
  };

  // ウォレット未接続時の表示
  if (!publicKey) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Paper
          sx={{
            p: 4,
            backgroundColor: '#002F2F',
            borderRadius: 2,
            maxWidth: 500,
            width: '100%',
          }}
          elevation={3}
        >
          <Typography
            variant="h4"
            gutterBottom
            sx={{ textAlign: 'center', color: 'white' }}
          >
            Wallet Not Connected
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'white', mt: 2, textAlign: 'center' }}
          >
            Please connect your wallet to view and withdraw your rewards.
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'lightblue', mt: 2, textAlign: 'center' }}
          >
            <Link
              href="https://phantom.app"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'lightblue' }}
            >
              Connect your wallet via Phantom
            </Link>
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <>
      {/* メインコンテンツ */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Paper
          sx={{
            p: 4,
            backgroundColor: '#002F2F',
            borderRadius: 2,
            maxWidth: 500,
            width: '100%',
          }}
          elevation={3}
        >
          {/* ヘッダー部分：タイトルを中央寄せ＆右側にリロードアイコン */}
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ width: '100%', textAlign: 'center', color: 'white' }}
            >
              Your Reward
            </Typography>
            <IconButton
              onClick={handleReload}
              sx={{ position: 'absolute', right: 0, color: 'white' }}
              disabled={withdrawing}
            >
              <RefreshIcon />
            </IconButton>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Box
                sx={{
                  backgroundColor: '#004747',
                  p: 2,
                  borderRadius: 1,
                  textAlign: 'center',
                }}
              >
                <Typography variant="h6" sx={{ color: 'lightgreen' }}>
                {rewardLoading
                  ? 'Loading...'
                  : localReward !== null
                  ? `${localReward} EMCII`
                  : 'Fail to fetch your balance'}
                 
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Typography
            variant="caption"
            display="block"
            align="center"
            sx={{ color: 'white', mt: 1 }}
          >
            * A fee of 0.000005 SOL is charged for reward withdrawal.
          </Typography>
          <Typography
            variant="caption"
            display="block"
            align="center"
            sx={{ color: 'white', mt: 1 }}
          >
            * It may take several tens of seconds to a few minutes for your updated balance to be reflected.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#004747',
                color: 'white',
                '&:hover': { backgroundColor: '#004747' },
              }}
              disabled={withdrawing || localReward === 0}
              onClick={handleWithdraw}
            >
              {withdrawing ? 'Withdrawing...' : 'Withdraw'}
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* ローディングオーバーレイ */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
        open={withdrawing}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* エラーメッセージ表示用 Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setErrorMessage('')}
          severity="error"
          sx={{ width: '100%', wordBreak: 'break-all' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* 成功メッセージ表示用 Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMessage('')}
          severity="success"
          sx={{ width: '100%', wordBreak: 'break-all' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default YourReward;

